/* Product tag: weyz-sizing-precis. Uses custom.tableau_des_tailles unchanged. */
(() => {
  if (customElements.get('weyz-product-sizing')) return;

  class WeyzProductSizing extends HTMLElement {
    static parseGrid(text) {
      if (typeof text !== 'string') return null;
      const match = text.trim().match(/^TAILLE:\s*([^\r\n]+)\r?\n\s*POIDS:\s*([^\r\n]+)\r?\n\s*SIZES:\s*\r?\n([\s\S]+)$/i);
      if (!match) return null;
      const axis = (line) => line.split(',').map((cell) => cell.trim() === '' ? NaN : Number(cell.trim()));
      const heights = axis(match[1]);
      const weights = axis(match[2]);
      const validAxis = (values) => values.length > 0 && values.every((value, i) =>
        Number.isInteger(value) && value > 0 && (i === 0 || value > values[i - 1]));
      if (!validAxis(heights) || !validAxis(weights)) return null;
      const sizes = match[3].trim().split(/\r?\n/).map((row) => row.split(',').map((cell) => cell.trim()));
      if (sizes.length !== weights.length || sizes.some((row) => row.length !== heights.length)) return null;
      return { heights, weights, sizes };
    }

    static recommend(grid, height, weight) {
      if (!grid) return null;
      const nearest = (values, value) => {
        if (!Number.isFinite(value) || value < values[0] || value > values[values.length - 1]) return -1;
        // Equal distance selects the higher reference, as on the landing page.
        return values.reduce((best, candidate, i) =>
          Math.abs(candidate - value) <= Math.abs(values[best] - value) ? i : best, 0);
      };
      const col = nearest(grid.heights, height);
      const row = nearest(grid.weights, weight);
      return col < 0 || row < 0 ? null : grid.sizes[row][col] || null;
    }

    connectedCallback() {
      // Defer one microtask so parser-inserted children are available too.
      queueMicrotask(() => { if (this.isConnected && !this.controller) this.init(); });
    }

    disconnectedCallback() {
      this.dialog?.close();
      this.controller?.abort();
      this.controller = null;
    }

    init() {
      this.dialog = this.querySelector('dialog');
      this.host = this.closest('product-info');
      if (!this.dialog || !this.host) return;
      this.controller = new AbortController();
      const listen = (node, event, handler) => node.addEventListener(event, handler, { signal: this.controller.signal });
      try { this.grid = WeyzProductSizing.parseGrid(this.querySelector('[data-wps-grid]').value); }
      catch { this.grid = null; }
      this.form = this.querySelector('[data-wps-form]');
      this.result = this.querySelector('[data-wps-result]');
      this.querySelector('[data-wps-error]').hidden = Boolean(this.grid);
      this.form.hidden = !this.grid;

      if (this.grid) {
        for (const [name, values, initial] of [['height', this.grid.heights, 180], ['weight', this.grid.weights, 70]]) {
          const range = this.querySelector(`[data-wps-range="${name}"]`);
          const number = this.querySelector(`[data-wps-number="${name}"]`);
          for (const input of [range, number]) {
            input.min = values[0];
            input.max = values[values.length - 1];
            input.value = Math.max(Number(input.min), Math.min(initial, Number(input.max)));
          }
          listen(range, 'input', () => { number.value = range.value; });
          listen(number, 'input', () => { if (number.validity.valid) range.value = number.value; });
        }
      }

      listen(this.host, 'click', (event) => {
        const opener = event.target.closest('[data-wps-open]');
        if (!opener || opener.closest('product-info') !== this.host) return;
        this.opener = opener;
        this.showForm();
        if (!this.dialog.open) this.dialog.showModal();
      });
      listen(this, 'click', (event) => {
        if (event.target.closest('[data-wps-close]')) this.dialog.close();
        if (event.target.closest('[data-wps-back]')) this.showForm();
      });
      listen(this.dialog, 'click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      listen(this.dialog, 'close', () => { if (this.opener?.isConnected) this.opener.focus({ preventScroll: true }); });
      listen(this.form, 'submit', (event) => {
        event.preventDefault();
        if (!this.form.reportValidity()) return;
        const height = this.querySelector('[data-wps-number="height"]').valueAsNumber;
        const weight = this.querySelector('[data-wps-number="weight"]').valueAsNumber;
        const size = WeyzProductSizing.recommend(this.grid, height, weight);
        this.form.hidden = true;
        this.result.hidden = false;
        this.querySelector('[data-wps-size]').textContent = size || '';
        this.querySelector('[data-wps-recommendation]').hidden = !size;
        this.querySelector('[data-wps-no-match]').hidden = Boolean(size);
        const note = this.querySelector('[data-wps-note]');
        if (note) note.hidden = !size;
        this.querySelector('[data-wps-unavailable]').hidden = !size || this.selectSize(size) !== 'unavailable';
        this.querySelector('[data-wps-back]').focus({ preventScroll: true });
      });
    }

    showForm() {
      this.form.hidden = !this.grid;
      this.result.hidden = true;
    }

    selectSize(size) {
      const index = Number(this.dataset.sizeOption);
      const picker = this.host.querySelector('variant-selects');
      if (!picker || !Number.isInteger(index) || index < 0) return 'no-picker';
      const group = picker.querySelectorAll('.product-form__input')[index];
      if (!group) return 'no-picker';
      const choices = [...group.querySelectorAll('input[type="radio"], option')];
      const normalize = (value) => value.trim().toUpperCase();
      const canonical = (value) => normalize(value).split('/')[0].trim().replace(/^2XL$/, 'XXL');
      let choice = choices.find((item) => normalize(item.value) === normalize(size));
      if (!choice) {
        const matches = choices.filter((item) => canonical(item.value) === canonical(size));
        if (matches.length === 1) choice = matches[0];
      }
      if (!choice || choice.disabled || choice.dataset.wpsAvailable === 'false' || choice.classList.contains('disabled')) return 'unavailable';
      if (choice.tagName === 'OPTION') {
        const select = choice.closest('select');
        if (select.disabled) return 'unavailable';
        if (select.value !== choice.value) {
          select.value = choice.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (!choice.checked) choice.click();
      return 'selected';
    }
  }
  customElements.define('weyz-product-sizing', WeyzProductSizing);
})();
